import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  "What can Saarthva procure?",
  "Can you help set up a new office?",
  "What is Operations as a Service?",
];

const GREETING =
  "Namaste! I'm Saarthi, Saarthva's AI assistant. Ask me anything about procurement, sourcing or operations support for your business.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(
    localStorage.getItem("sv_chat_sid") ||
      (() => {
        const id = crypto.randomUUID();
        localStorage.setItem("sv_chat_sid", id);
        return id;
      })()
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            const piece = parsed.t || parsed.error || "";
            if (piece) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + piece };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Connection hiccup — please try again, or WhatsApp us at +91 92126 36329.",
        };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen(!open)}
        aria-label="Chat with Saarthi, Saarthva's AI assistant"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-navy text-white shadow-xl shadow-navy/25 grid place-items-center hover:bg-saffron transition-colors"
      >
        {open ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 left-6 z-50 w-[calc(100vw-3rem)] max-w-sm rounded-3xl border border-line bg-white shadow-2xl shadow-navy/20 overflow-hidden flex flex-col"
        >
          <div className="bg-navy text-white px-5 py-4 flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-saffron grid place-items-center shrink-0">
              <Sparkles className="w-4.5 h-4.5 text-white w-5 h-5" />
            </span>
            <div>
              <p className="font-display font-bold leading-tight">Ask Saarthi</p>
              <p className="text-[11px] text-slate-400">AI assistant · replies instantly</p>
            </div>
          </div>

          <div ref={scrollRef} className="h-80 overflow-y-auto p-4 space-y-3 bg-mist" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-navy text-white rounded-br-md"
                    : "mr-auto bg-white border border-line text-slate-700 rounded-bl-md"
                }`}
              >
                {m.content || (
                  <span className="inline-flex gap-1 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce [animation-delay:0.15s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce [animation-delay:0.3s]" />
                  </span>
                )}
              </div>
            ))}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {CHIPS.map((c) => (
                  <button
                    key={c}
                    onClick={() => send(c)}
                    data-testid={`chat-chip-${c.slice(0, 20).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-xs font-semibold text-steel bg-white border border-steel/30 rounded-full px-3 py-2 hover:bg-steel hover:text-white transition-colors"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 p-3 border-t border-line bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              data-testid="chat-input"
              className="flex-1 rounded-full border border-line bg-mist px-4 py-2.5 text-sm text-navy placeholder:text-slate-400 outline-none focus:border-steel"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="chat-send-button"
              aria-label="Send message"
              className="w-10 h-10 rounded-full bg-saffron hover:bg-saffrondark disabled:opacity-50 text-white grid place-items-center transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <p className="text-[10px] text-slate-400 text-center pb-2.5 bg-white">
            AI can make mistakes — for firm quotes, use Request a Quote.
          </p>
        </div>
      )}
    </>
  );
}
